import clsx from 'clsx'

export interface ZPlane {
  z: number
  mediaVersionId: string
  /** point + circle annotations at this focal plane */
  count: number
}

interface ZPlaneChipsProps {
  planes: ZPlane[]
  /** null = All planes */
  selectedZ: number | null
  totalCount: number
  onSelect: (z: number | null) => void
}

/** Focal-plane selector: strict per-plane filter chips (Z1–Z7 + All) with
 *  annotation counts. Selecting a plane switches BOTH the displayed image
 *  plane and the annotation filter; All shows every annotation on the
 *  current image plane. Hidden entirely for single-plane media. */
export function ZPlaneChips({ planes, selectedZ, totalCount, onSelect }: ZPlaneChipsProps) {
  if (planes.length < 2) return null

  const chip = (label: string, value: number | null, count: number) => {
    const active = selectedZ === value
    return (
      <button
        key={label}
        type="button"
        onClick={() => onSelect(value)}
        aria-pressed={active}
        title={value === null ? `All planes (${count} annotations)` : `Focal plane ${label} (${count} annotations)`}
        className={clsx(
          'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12.5px] font-semibold transition-colors',
          active
            ? 'border-transparent bg-fill-primary text-white'
            : // transparent over the chrome panel: text-primary was white-on-white
              // in dark mode with the old bg-white/80 pills
              'border-border-default/50 bg-transparent text-text-primary hover:bg-[color-mix(in_srgb,var(--text-primary)_8%,transparent)]',
        )}
      >
        {label}
        <span className={clsx('text-[11px] font-normal', active ? 'text-white/70' : 'text-text-secondary')}>
          {count}
        </span>
      </button>
    )
  }

  return (
    <div className="chrome-panel pointer-events-auto flex items-center gap-1.5 rounded-modal border border-border-default/40 px-2 py-1.5">
      {chip('All', null, totalCount)}
      {planes.map((p) => chip(`Z${p.z}`, p.z, p.count))}
    </div>
  )
}
